import type { Content, CustomTableLayout, TDocumentDefinitions } from 'pdfmake/interfaces';
import { Questionnaire, QuestionnaireResponse } from '../../types/fhir';
import { QuestionnaireKind } from '../../types/QuestionnaireKind';
import {
    getAvailableAudiences,
    getDefaultAudience,
    getMaterialDocument,
    resolveTherapyMaterialBlocks,
    ResolvedTherapyMaterialBlock,
    TherapyMaterialAudience,
    TherapyMaterialContentItem,
    TherapyMaterialDocument,
} from '../../components/TherapyMaterial/therapyMaterial';
import { getAssetFileName, PdfImage } from '../appAssets';
import { extractIdFromReference, firstNonEmpty } from '../questionnaireResponseCsv';
import { buildQuestionnaireResponseOutline, OutlineNode } from './questionnaireResponseOutline';

// The t function of react-i18next's useTranslation.
export type Translate = (key: string, options?: Record<string, unknown>) => string;

export type ResponsePdfTask = {
    id: string;
    patientName?: string;
    patientId?: string;
    questionnaireName?: string;
    status?: string;
    startsOn?: string;
    endsOn?: string;
};

export type ResponsePdfInput = {
    task: ResponsePdfTask;
    kind: QuestionnaireKind;
    questionnaire: Questionnaire | null;
    questionnaireResponse: QuestionnaireResponse;
    // Content version of therapy material; defaults to the version the app shows by default.
    audience?: TherapyMaterialAudience;
    t: Translate;
    locale: string;
    exportedAt: Date;
};

// Images keyed by their app asset path; null marks an image the editor does not have.
export type ResponsePdfImages = Map<string, PdfImage | null>;

// DIN A4 in pt.
export const A4_WIDTH = 595.28;
export const A4_HEIGHT = 841.89;
export const PAGE_MARGIN_X = 57;
export const PAGE_MARGIN_TOP = 80;
export const PAGE_MARGIN_BOTTOM = 60;
const CONTENT_WIDTH = A4_WIDTH - 2 * PAGE_MARGIN_X;
const MAX_IMAGE_HEIGHT = A4_HEIGHT * 0.36;
// Marks headings that must not end up alone at the bottom of a page.
const HEADING_LEVEL = 1;
const SMALL_IMAGE_SIZE = 140;
// Answers longer than this may flow across pages instead of being kept with their question.
const MAX_UNBREAKABLE_ANSWER_LENGTH = 1500;

export const COLORS = {
    text: '#25313c',
    muted: '#5f6f7d',
    blue: '#004c97',
    blueLight: '#eaf3fb',
    answerFill: '#f3f6f9',
    border: '#d6e0e8',
    check: '#2e7d32',
};

const CHECK_ICON_SVG = `<svg width="12" height="12" viewBox="0 0 24 24"><path d="M4 12.5l5 5L20 6.5" fill="none" stroke="${COLORS.check}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const answerBoxLayout: CustomTableLayout = {
    hLineWidth: () => 0.5,
    vLineWidth: () => 0.5,
    hLineColor: () => COLORS.border,
    vLineColor: () => COLORS.border,
    paddingLeft: () => 8,
    paddingRight: () => 8,
    paddingTop: () => 6,
    paddingBottom: () => 6,
    fillColor: () => COLORS.answerFill,
};

const calloutLayout: CustomTableLayout = {
    hLineWidth: () => 0,
    vLineWidth: (index) => (index === 0 ? 3 : 0),
    vLineColor: () => COLORS.blue,
    paddingLeft: () => 10,
    paddingRight: () => 10,
    paddingTop: () => 7,
    paddingBottom: () => 7,
    fillColor: () => COLORS.blueLight,
};

const imagePlaceholderLayout: CustomTableLayout = {
    hLineWidth: () => 1,
    vLineWidth: () => 1,
    hLineColor: () => COLORS.border,
    vLineColor: () => COLORS.border,
    hLineStyle: () => ({ dash: { length: 4 } }),
    vLineStyle: () => ({ dash: { length: 4 } }),
    paddingTop: () => 18,
    paddingBottom: () => 18,
};

const metadataLayout: CustomTableLayout = {
    hLineWidth: (index, node) => (index === 0 || index === node.table.body.length ? 0 : 0.5),
    vLineWidth: () => 0,
    hLineColor: () => COLORS.border,
    paddingLeft: () => 0,
    paddingTop: () => 4,
    paddingBottom: () => 4,
};

export const getPatientIdentity = (
    task: ResponsePdfTask,
    questionnaireResponse: QuestionnaireResponse,
): { name?: string; id?: string } => ({
    name: firstNonEmpty(
        task.patientName,
        questionnaireResponse.subject?.display,
        questionnaireResponse.author?.display,
    ),
    id: firstNonEmpty(
        task.patientId,
        extractIdFromReference(questionnaireResponse.subject?.reference),
        extractIdFromReference(questionnaireResponse.author?.reference),
    ),
});

const getResponseTitle = (
    task: ResponsePdfTask,
    questionnaire: Questionnaire | null,
    materialDocument?: TherapyMaterialDocument | null,
): string | undefined =>
    firstNonEmpty(
        materialDocument?.title,
        questionnaire?.title,
        questionnaire?.name,
        task.questionnaireName,
        task.id,
    )?.trim();

export const getKindLabel = (kind: QuestionnaireKind, t: Translate): string =>
    kind === 'nachsorge' ? t('Aftercare') : kind === 'therapy-material' ? t('Therapy Material') : t('Questionnaire');

export const formatDateTime = (value: string | undefined, locale: string): string | undefined => {
    if (!value) {
        return undefined;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? value
        : date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
};

const parseMaterialDocument = (
    questionnaire: Questionnaire | null,
    kind: QuestionnaireKind,
): TherapyMaterialDocument | null => {
    if (!questionnaire || kind === 'questionnaire') {
        return null;
    }
    try {
        return getMaterialDocument(questionnaire, kind);
    } catch {
        return null;
    }
};

export const getResponsePdfTitle = ({
    task,
    questionnaire,
    kind,
}: Pick<ResponsePdfInput, 'task' | 'questionnaire' | 'kind'>): string | undefined =>
    getResponseTitle(task, questionnaire, parseMaterialDocument(questionnaire, kind));

const resolveAudience = (
    document: TherapyMaterialDocument,
    audience?: TherapyMaterialAudience,
): TherapyMaterialAudience =>
    audience && getAvailableAudiences(document).includes(audience) ? audience : getDefaultAudience(document);

// The app asset paths of all images the PDF of this response shows.
export const collectResponsePdfImagePaths = ({
    questionnaire,
    kind,
    audience,
}: Pick<ResponsePdfInput, 'questionnaire' | 'kind' | 'audience'>): string[] => {
    const document = parseMaterialDocument(questionnaire, kind);
    if (!document) {
        return [];
    }
    const content = document.content[resolveAudience(document, audience)] ?? [];
    return Array.from(
        new Set(
            content
                .filter((item) => ['image', 'imageform', 'thoughtclouds'].includes(item.type) && item.path)
                .map((item) => item.path as string),
        ),
    );
};

const answerBox = (answers: string[], noAnswerText: string): Content => ({
    table: {
        widths: ['*'],
        body: [[answers.length > 0 ? { text: answers.join('\n') } : { text: noAnswerText, style: 'unanswered' }]],
    },
    layout: answerBoxLayout,
    margin: [0, 0, 0, 10],
});

const questionWithAnswer = (questionText: string, answers: string[], noAnswerText: string): Content => ({
    stack: [{ text: questionText, style: 'question' }, answerBox(answers, noAnswerText)],
    unbreakable: answers.join('\n').length < MAX_UNBREAKABLE_ANSWER_LENGTH,
});

const callout = (content: Content[]): Content => ({
    table: { widths: ['*'], body: [[{ stack: content }]] },
    layout: calloutLayout,
    margin: [0, 2, 0, 10],
});

export const buildResponsePdfDefinition = (
    input: ResponsePdfInput,
    images: ResponsePdfImages,
): TDocumentDefinitions => {
    const { task, kind, questionnaire, questionnaireResponse, t, locale, exportedAt } = input;
    const labels = { yes: t('Yes'), no: t('No') };
    const noAnswerText = t('No answer provided');
    const kindLabel = getKindLabel(kind, t);
    const patient = getPatientIdentity(task, questionnaireResponse);
    const patientLabel =
        patient.name && patient.id ? `${patient.name} (${patient.id})` : patient.name || patient.id || t('Unknown');
    const materialDocument = parseMaterialDocument(questionnaire, kind);
    const audience = materialDocument ? resolveAudience(materialDocument, input.audience) : undefined;
    const title = getResponseTitle(task, questionnaire, materialDocument) || kindLabel;
    const imageDictionary: Record<string, string> = {};
    const imageKeys = new Map<string, string>();

    const imageContent = (item: TherapyMaterialContentItem, size: 'full' | 'small'): Content[] => {
        const caption: Content[] = item.header ? [{ text: item.header, style: 'caption' }] : [];
        const image = item.path ? images.get(item.path) : undefined;
        if (!item.path || !image) {
            return [
                ...caption,
                {
                    table: {
                        widths: ['*'],
                        body: [
                            [
                                {
                                    text: t('Image not available: {{file}}', { file: getAssetFileName(item.path) }),
                                    style: 'imagePlaceholder',
                                },
                            ],
                        ],
                    },
                    layout: imagePlaceholderLayout,
                    margin: [0, 2, 0, 10],
                },
            ];
        }

        let key = imageKeys.get(item.path);
        if (!key) {
            key = `image-${imageKeys.size}`;
            imageKeys.set(item.path, key);
            imageDictionary[key] = image.dataUrl;
        }
        return [
            {
                stack: [
                    ...caption,
                    {
                        image: key,
                        fit:
                            size === 'small' ? [SMALL_IMAGE_SIZE, SMALL_IMAGE_SIZE] : [CONTENT_WIDTH, MAX_IMAGE_HEIGHT],
                        alignment: 'center',
                        margin: [0, 2, 0, 10],
                    },
                ],
                unbreakable: true,
            },
        ];
    };

    const materialBlockContent = ({ item, answers }: ResolvedTherapyMaterialBlock): Content[] => {
        switch (item.type) {
            case 'header':
                return [{ text: item.text ?? '', style: 'materialHeading', headlineLevel: HEADING_LEVEL }];
            case 'paragraph':
                return [{ text: item.text ?? '', style: 'paragraph' }];
            case 'display':
                return [callout([{ text: item.text ?? '', bold: true }])];
            case 'image':
                return imageContent(item, 'full');
            case 'check':
                return [
                    {
                        columns: [
                            { svg: CHECK_ICON_SVG, width: 12, margin: [0, 1, 0, 0] },
                            { text: item.text ?? '', width: '*' },
                        ],
                        columnGap: 8,
                        margin: [0, 0, 0, 6],
                    },
                ];
            case 'listitem':
                return [
                    {
                        columns: [
                            { text: '•', width: 10, color: COLORS.blue, bold: true },
                            { text: item.text ?? '', width: '*' },
                        ],
                        columnGap: 4,
                        margin: [8, 0, 0, 4],
                    },
                ];
            case 'chat':
                return [
                    callout([
                        ...(item.character ? [{ text: item.character, bold: true, color: COLORS.blue }] : []),
                        { text: item.text ?? '' },
                    ]),
                ];
            case 'textfield':
                return [questionWithAnswer(item.text ?? '', answers[0] ? [answers[0]] : [], noAnswerText)];
            case 'imageform':
                return [
                    ...imageContent(item, 'full'),
                    { text: t('Entries on this image'), style: 'question' },
                    {
                        ol: answers.map((answer) =>
                            answer ? { text: answer } : { text: noAnswerText, style: 'unanswered' },
                        ),
                        margin: [0, 0, 0, 10],
                    },
                ];
            case 'thoughtclouds':
                return [
                    ...(item.path ? imageContent(item, 'small') : []),
                    ...(item.prompts ?? []).map((prompt, promptIndex) =>
                        questionWithAnswer(prompt, answers[promptIndex] ? [answers[promptIndex]] : [], noAnswerText),
                    ),
                ];
            default:
                return [{ text: `${t('Unsupported therapy material block')}: ${item.type}`, style: 'notice' }];
        }
    };

    const outlineContent = (nodes: OutlineNode[], level = 0): Content[] =>
        nodes.flatMap((node): Content[] => {
            const label = [node.kind !== 'display' ? node.prefix : undefined, node.text].filter(Boolean).join(' ');
            switch (node.kind) {
                case 'group':
                    return [
                        ...(label
                            ? [
                                  {
                                      text: node.occurrence ? `${label} (${node.occurrence})` : label,
                                      style: level === 0 ? 'groupTitle' : 'subgroupTitle',
                                      headlineLevel: HEADING_LEVEL,
                                  },
                              ]
                            : []),
                        ...outlineContent(node.children, level + 1),
                    ];
                case 'display':
                    return [{ text: node.text, style: 'displayText' }];
                case 'question':
                    return [
                        questionWithAnswer(label || node.linkId, node.answers, noAnswerText),
                        ...outlineContent(node.children, level + 1),
                    ];
            }
        });

    const buildBody = (): Content[] => {
        if (materialDocument && questionnaire && audience) {
            return resolveTherapyMaterialBlocks({
                document: materialDocument,
                audience,
                questionnaire,
                questionnaireResponse,
                labels,
            }).flatMap(materialBlockContent);
        }

        const notice: Content[] =
            kind !== 'questionnaire' || !questionnaire
                ? [
                      {
                          text: t('The material could not be loaded. Only the saved answers are shown.'),
                          style: 'notice',
                      },
                  ]
                : [];
        const outline = buildQuestionnaireResponseOutline({
            questionnaire: kind === 'questionnaire' ? questionnaire : null,
            questionnaireResponse,
            labels,
        });
        return outline.length > 0
            ? [...notice, ...outlineContent(outline)]
            : [...notice, { text: t('No answers provided in this response.'), style: 'unanswered' }];
    };

    const metadataRows: Array<[string, string | undefined]> = [
        [t('Patient'), patient.name],
        [t('User ID'), patient.id],
        [kindLabel, title],
        [
            t('Content version'),
            kind === 'therapy-material' && materialDocument && getAvailableAudiences(materialDocument).length > 1
                ? audience === 'male'
                    ? t('Male version')
                    : t('Female version')
                : undefined,
        ],
        [t('Task ID'), task.id],
        [t('Task Status'), task.status],
        [t('Available from'), formatDateTime(task.startsOn, locale)],
        [t('Available until'), formatDateTime(task.endsOn, locale)],
        [t('Submitted'), formatDateTime(questionnaireResponse.authored, locale)],
        [t('Last Updated'), formatDateTime(questionnaireResponse.meta?.lastUpdated, locale)],
        [t('Response Status'), questionnaireResponse.status],
        [t('Response ID'), questionnaireResponse.id],
    ];

    const subtitle =
        kind === 'nachsorge' && materialDocument?.week !== undefined
            ? `${kindLabel} · ${t('Aftercare week {{week}}', { week: materialDocument.week })}`
            : kindLabel;
    const sectionTitle =
        kind === 'nachsorge'
            ? t('Aftercare Material and Answers')
            : kind === 'therapy-material'
            ? t('Therapy Material and Answers')
            : t('Answers');
    const exportedAtLabel = formatDateTime(exportedAt.toISOString(), locale);
    const body = buildBody();

    return {
        pageSize: 'A4',
        pageOrientation: 'portrait',
        pageMargins: [PAGE_MARGIN_X, PAGE_MARGIN_TOP, PAGE_MARGIN_X, PAGE_MARGIN_BOTTOM],
        info: {
            title: `${title} – ${patientLabel}`,
            subject: kindLabel,
            creator: 'KoALA Questionnaire Editor',
        },
        header: (): Content => ({
            margin: [PAGE_MARGIN_X, 30, PAGE_MARGIN_X, 0],
            stack: [
                {
                    columns: [
                        { text: `KoALA · ${kindLabel}`, style: 'pageHeader' },
                        { text: patientLabel, style: 'pageHeaderPatient', alignment: 'right' },
                    ],
                },
                {
                    canvas: [
                        {
                            type: 'line',
                            x1: 0,
                            y1: 6,
                            x2: CONTENT_WIDTH,
                            y2: 6,
                            lineWidth: 0.5,
                            lineColor: COLORS.border,
                        },
                    ],
                },
            ],
        }),
        footer: (currentPage: number, pageCount: number): Content => ({
            margin: [PAGE_MARGIN_X, 24, PAGE_MARGIN_X, 0],
            columns: [
                { text: t('Exported on {{date}}', { date: exportedAtLabel }), style: 'pageFooter' },
                {
                    text: t('Page {{page}} of {{pages}}', { page: currentPage, pages: pageCount }),
                    style: 'pageFooter',
                    alignment: 'right',
                },
            ],
        }),
        content: [
            { text: title, style: 'title' },
            { text: subtitle, style: 'subtitle' },
            {
                table: {
                    widths: [130, '*'],
                    body: metadataRows
                        .filter((row): row is [string, string] => Boolean(row[1]))
                        .map(([label, value]) => [
                            { text: label, style: 'metaLabel' },
                            { text: value, style: 'metaValue' },
                        ]),
                },
                layout: metadataLayout,
            },
            { text: sectionTitle, style: 'sectionTitle', headlineLevel: HEADING_LEVEL },
            ...body,
        ],
        images: imageDictionary,
        pageBreakBefore: (currentNode, followingNodesOnPage) =>
            currentNode.headlineLevel === HEADING_LEVEL && followingNodesOnPage.length === 0,
        defaultStyle: { font: 'Roboto', fontSize: 10, lineHeight: 1.25, color: COLORS.text },
        styles: {
            title: { fontSize: 18, bold: true, color: COLORS.blue, margin: [0, 0, 0, 2] },
            subtitle: { fontSize: 10, color: COLORS.muted, margin: [0, 0, 0, 12] },
            sectionTitle: { fontSize: 13, bold: true, color: COLORS.blue, margin: [0, 18, 0, 8] },
            groupTitle: { fontSize: 12, bold: true, color: COLORS.blue, margin: [0, 10, 0, 6] },
            subgroupTitle: { fontSize: 11, bold: true, margin: [0, 8, 0, 4] },
            question: { bold: true, margin: [0, 4, 0, 4] },
            unanswered: { italics: true, color: COLORS.muted },
            displayText: { italics: true, color: COLORS.muted, margin: [0, 2, 0, 8] },
            materialHeading: { fontSize: 13, bold: true, color: COLORS.blue, margin: [0, 10, 0, 6] },
            paragraph: { lineHeight: 1.35, margin: [0, 0, 0, 8] },
            caption: { bold: true, margin: [0, 4, 0, 4] },
            imagePlaceholder: { italics: true, color: COLORS.muted, alignment: 'center' },
            notice: { italics: true, color: COLORS.muted, margin: [0, 0, 0, 10] },
            metaLabel: { fontSize: 9, color: COLORS.muted },
            metaValue: { fontSize: 9 },
            pageHeader: { fontSize: 8, color: COLORS.muted },
            pageHeaderPatient: { fontSize: 8, bold: true },
            pageFooter: { fontSize: 8, color: COLORS.muted },
        },
    };
};
